import { Segmented, theme } from 'antd'
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { formatBytes } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { formatInt, useChartColors } from '@/Ship/Components/Dashboard/Overview/shared'
import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_ADMIN_TREND_DAYS } from '../Contracts/DiskAdmin'
import { trendData, trendDelta } from '../Helpers/diskAdminCharts'
import { formatDate, formatDayShort } from '../Helpers/diskAdminFormat'

import type { TrendDatum } from '../Helpers/diskAdminCharts'
import type { DiskAdminTrend, DiskAdminTrendDays } from '../Contracts/DiskAdmin'

import overview from '@/Ship/Components/Dashboard/Overview/Overview.module.css'
import styles from './DiskAdmin.module.css'

/** Назва серії — і в легенді, і в підказці. */
export const TREND_SERIES = 'Зайнято'

interface TrendTooltipProps {
    active?: boolean
    payload?: Array<{ payload: TrendDatum }>
    color: string
}

/** Підказка називає серію й день, додає файли, старі версії й кошик. */
const TrendTooltip = ({ active, payload, color }: TrendTooltipProps) => {
    const { token } = theme.useToken()
    const point = active ? payload?.[0]?.payload : undefined

    if (!point) return null

    const row = (swatch: string | null, name: string, value: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', minWidth: 180 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: token.colorTextSecondary }}>
                {swatch ? <span className={classNames(overview['swatch'])} style={{ background: swatch }} /> : null}
                {name}
            </span>
            <b style={{ color: token.colorText, fontVariantNumeric: 'tabular-nums' }}>{value}</b>
        </div>
    )

    return (
        <div
            style={{
                padding: '10px 12px',
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 12,
            }}
        >
            <div style={{ color: token.colorText, fontWeight: 600, marginBottom: 2 }}>
                {formatDate(point.day)}
                {point.live ? ' · зараз' : ''}
            </div>
            {row(color, TREND_SERIES, formatBytes(point.used))}
            {row(null, 'Файлів', formatInt(point.files))}
            {row(null, 'Старі версії', formatBytes(point.oldVersions))}
            {row(null, 'Кошик', formatBytes(point.trash))}
        </div>
    )
}

interface UsageTrendProps {
    trend: DiskAdminTrend
    height?: number
    /** Перемикач вікна 30 / 90 / 365 днів; без нього — фіксоване вікно (міні-тренд картки). */
    days?: DiskAdminTrendDays
    onDaysChange?: (days: DiskAdminTrendDays) => void
}

/**
 * Тренд зайнятого місця — лінія одного ряду з нічних знімків і сьогоднішньої
 * живої точки; пунктир — ліміт тенанта, якщо він заданий (`DISK-006` §5.3).
 *
 * ⚠️ На свіжому стенді знімок один: малюємо **одну точку** з поясненням,
 * звідки візьметься лінія, — не порожню вісь і не вигадану пряму.
 */
export const UsageTrend = ({ trend, height = 220, days, onDaysChange }: UsageTrendProps) => {
    const { token } = theme.useToken()
    const { primary } = useChartColors()
    const { data, single } = trendData(trend.points)
    const delta = trendDelta(data)

    return (
        <div data-chart="disk-trend" data-points={data.length}>
            <div className={classNames(overview['legend'])} style={{ marginTop: 0, marginBottom: 8, justifyContent: 'space-between' }}>
                <span className={classNames(overview['legendItem'])}>
                    <span className={classNames(overview['swatch'])} style={{ background: primary }} />
                    {TREND_SERIES}
                    {delta !== null ? (
                        <b>
                            {delta >= 0 ? '+' : '−'}
                            {formatBytes(Math.abs(delta))} за період
                        </b>
                    ) : null}
                </span>
                {trend.quota !== null ? (
                    <span className={classNames(overview['legendItem'])}>
                        <span style={{ width: 14, borderTop: `2px dashed ${token.colorError}` }} />
                        Ліміт {formatBytes(trend.quota)}
                    </span>
                ) : null}
                {days !== undefined && onDaysChange ? (
                    <Segmented
                        size="small"
                        aria-label="Період тренду"
                        value={days}
                        onChange={(value) => onDaysChange(value as DiskAdminTrendDays)}
                        options={DISK_ADMIN_TREND_DAYS.map((value) => ({ value, label: `${value} дн.` }))}
                    />
                ) : null}
            </div>
            <ResponsiveContainer width="100%" height={single ? Math.min(height, 160) : height}>
                <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={token.colorBorderSecondary} />
                    <XAxis
                        dataKey="day"
                        tickFormatter={formatDayShort}
                        tick={{ fill: token.colorTextTertiary, fontSize: 12 }}
                        stroke={token.colorBorder}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={16}
                    />
                    <YAxis
                        tickFormatter={(value: number) => formatBytes(value)}
                        tick={{ fill: token.colorTextTertiary, fontSize: 12 }}
                        stroke={token.colorBorder}
                        axisLine={false}
                        tickLine={false}
                        width={64}
                        domain={[0, (max: number) => Math.max(max, trend.quota ?? 0) * 1.1 || 1]}
                    />
                    <Tooltip content={<TrendTooltip color={primary} />} cursor={{ stroke: token.colorBorder }} />
                    {trend.quota !== null ? (
                        <ReferenceLine y={trend.quota} stroke={token.colorError} strokeDasharray="6 4" ifOverflow="extendDomain" />
                    ) : null}
                    <Line
                        type="monotone"
                        dataKey="used"
                        name={TREND_SERIES}
                        stroke={primary}
                        strokeWidth={2}
                        // Одна точка — власне коло: тема перефарбовує `.recharts-line-dot` у колір тла
                        dot={
                            single
                                ? (props: { cx?: number; cy?: number }) => (
                                      <circle key="single" cx={props.cx} cy={props.cy} r={6} fill={primary} data-trend-dot />
                                  )
                                : false
                        }
                        activeDot={{ r: 5 }}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
            {single ? (
                <div className={classNames(styles['trendNote'])} data-trend-single>
                    Поки одна точка — {formatDate(data[0]?.day)}. Лінія з’явиться після нічних звірок: щоночі
                    додається знімок дня.
                </div>
            ) : null}
        </div>
    )
}
